import { useState, useRef, useContext, useEffect } from "react"; // Added useRef to handle file input reference
import { Button, Form, Row, Col, DatePicker, Spin, Space } from "antd";
import FormLayout, {
  FormContent,
  FormHeader,
} from "../../../../components/form/form";
import {
  UploadOutlined,
  DownloadOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import AuthUtil from "../../../../utils/auth";
import { TResponseModel } from "../../../../types/config";
import {
  GetBulkUploadSample,
  GetCustomerList,
  ImportDealerList,
  SaveBulkDealerList,
} from "../../../../utils/api.constant";
import {
  eBULKUPLOADTEMPLATE,
  eHTTPStatusCode,
  eResultCode,
} from "../../../../utils/enum";
import { TBulkUploadResponse } from "../../../../types";
import CustomDropdown from "../../../../components/custom-dropdown/custom-dropdown";
import CustomModal from "../../../../components/modal";
import BulkUploadApprovalList from "../dealer/bulk-upload-approval-list";
import { UserContext } from "../../../../context";
import {
  STAKE_HOLDERS,
  UNAUTHORIZEDREQUEST,
} from "../../../../utils/constants";
import dayjs from "dayjs";

type DrawerProps = {
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  orgType?: string;
};

/**
 * Component for handling bulk upload functionality.
 * Allows users to upload XLSX files and process the data.
 */
export default function BulkUploadForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [file, setFile] = useState<File | null>(null); // Store the uploaded file
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref to handle file input
  const [selectedName, setSelectedName] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEligible, setIsEligible] = useState<boolean>(true);
  const [bulkUploadResponse, setBulkUploadResponse] =
    useState<TBulkUploadResponse>();
  const context: any = useContext(UserContext);

  useEffect(() => {
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      form.setFieldValue("customerId", context?.user?.orgId);
      setSelectedName(context?.user?.orgName);
    }
  }, [context?.user]);

  /**
   * Handles file selection from the input.
   * Validates the file type and updates the state accordingly.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the file input
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      !form.getFieldValue("customerId") ||
      !form.getFieldValue("uploadMonth")
    ) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please fill all the mandatory fields",
      });
      return;
    }
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const isXlsx =
        selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!isXlsx) {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Please upload an XLSX file!",
        });
        setFile(null); // Reset file state
        event.target.value = ""; // Clear the input
      } else {
        setFile(selectedFile); // Set the selected file
        handleSubmit(selectedFile);
      }
    }

    // Reset the input value to ensure `onChange` is triggered for the same file
    event.target.value = "";
  };

  /**
   * Handles the submission of the bulk upload form.
   * Validates the file and if valid, sends a POST request to the server to process the file.
   * If the response is successful, refreshes the list and closes the drawer.
   * If the response is not successful, shows an error toast with the error message.
   */
  const handleSubmit = async (file: any) => {
    if (!file) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please select an XLSX file!",
      });
      return;
    }

    // Implement your file upload logic here
    const formData = new FormData();
    formData.append("file", file);
    formData.append("customerId", form.getFieldValue("customerId"));

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${ImportDealerList}`,
        {
          method: "POST",
          body: formData,

          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );
      if (response.status === eHTTPStatusCode.OK) {
        const responseData = (await response.json()) as TResponseModel;

        if (responseData.dataResponse.returnCode === eResultCode.SUCCESS) {
          setIsEligible(responseData?.data?.error ? false : true);
          setBulkUploadResponse(responseData.data);
          setIsModalOpen(true);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: responseData.dataResponse.description,
          });
        }
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error uploading file",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to download bulk upload sample template
  const handleDownload = async () => {
    if (!form.getFieldValue("customerId")) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please select customer to complete this operation",
      });
      return;
    }
    try {
      // Start loading
      setIsLoading(true);

      // Fetch the XLSX file
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetBulkUploadSample}?SampleId=${eBULKUPLOADTEMPLATE.ACTIVEDEALERTEMPLATE}&customerId=${form.getFieldValue("customerId")}`,
        {
          method: "GET",
          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = `${selectedName}_ADL_Upload_Sample.xlsx`; // Set the desired file name with .xlsx extension

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "File downloaded successfully!",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content:
            "Error while downloading sample file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error downloading file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  // Function to trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handles the OK button click on the modal, logs the user details
   * and closes the modal.
   * @param {TDealer} detalerDetails - The dealer details object.
   */
  const handleOk = async () => {
    try {
      // setIsLoading(true);
      setIsEligible(false);
      const requestPayload = {
        data: {
          customerId: form.getFieldValue("customerId"),
          ADLBulkUploadTempId: bulkUploadResponse?.id,
          IsIDEXModifiedADLUpload: false,
          uploadMonthDate: form.getFieldValue("uploadMonth"),
        },
      };
      const response: TResponseModel = await post(
        SaveBulkDealerList,
        requestPayload
      );
      const { dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // setIsLoading(false);
        setIsEligible(true);
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        form.resetFields();
        setFile(null);
        setIsModalOpen(false);
        props.onCloseDrawer();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        // setIsLoading(false);
        setIsEligible(true);
      }
    } catch (error) {
      console.error("Error:", error);
      // setIsLoading(false);
      setIsEligible(true);
    }
  };

  /**
   * Handles the cancel button click on the modal, and closes the modal.
   */
  const handleCancel = () => {
    setIsModalOpen(false);
    setFile(null);
  };
  const contentStyle: React.CSSProperties = {
    padding: 50,
  };

  const content = <div style={contentStyle} />;
  return (
    <FormLayout>
      <CustomModal
        title={"ADL Upload Status"}
        useCustomHeader={true}
        isOpen={isModalOpen}
        // isFullScreen={false}
        onOk={() => handleOk()} // Passing the custom function
        onCancel={handleCancel}
        okText={
          context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
            ? "Submit"
            : "Approve"
        }
        disableOk={!isEligible}
      >
        <BulkUploadApprovalList
          customerId={form.getFieldValue("customerId")}
          customerName={selectedName}
          fileName={file?.name}
          // dealers={uploadedDealers}
          bulkUploadResponse={bulkUploadResponse}
        ></BulkUploadApprovalList>
      </CustomModal>
      <Form
        onFinish={handleSubmit}
        layout="vertical"
        name="bulkUploadForm"
        form={form}
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              form.resetFields();
              setFile(null);
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  Import Active Dealer List
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[{ required: true, message: "Please choose customer" }]}
              >
                <CustomDropdown
                  isDisabled={
                    context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER ||
                    isLoading
                      ? true
                      : false
                  }
                  endPoint={GetCustomerList}
                  value={form.getFieldValue("customerId")}
                  selectedName={selectedName}
                  onChange={(selected: any) => {
                    if (selected?.servicePartners?.length) {
                      form.setFieldValue("customerId", selected.value);
                      setSelectedName(selected.label);
                    } else {
                      form.setFieldValue("customerId", null);
                      setSelectedName("");
                      onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.centerTop,
                        content:
                          "Service Partner is not mapped with this customer",
                      });
                    }
                  }}
                  label=""
                  placeHolder="Select customer"
                ></CustomDropdown>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="uploadMonth"
                label="Upload Month"
                rules={[
                  {
                    required: true,
                    message: "Please select upload month",
                  },
                ]}
              >
                <DatePicker
                  disabled={isLoading}
                  picker="month" // Use Month Picker
                  placeholder="Select upload month"
                  disabledDate={(current) => {
                    // Disable past months
                    return current && current < dayjs().startOf("month");
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            {isLoading ? (
              <Col
                span={24}
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: "2rem",
                  flexDirection: "column",
                }}
              >
                <Spin
                  tip={
                    <span
                      style={{
                        color: "#fff",
                        fontFamily: "Montserrat-Regular",
                      }}
                    >
                      Validating . . .
                    </span>
                  }
                  size="large"
                >
                  {content}
                </Spin>
              </Col>
            ) : (
              <Col
                span={24}
                style={{
                  justifyContent: "flex-start",
                  display: "flex",
                  marginTop: "2rem",
                  flexDirection: "column",
                }}
              >
                <Form.Item
                  label=""
                  style={{ marginBottom: 0 }}
                  // dependencies={["customerId", "uploadMonth"]}
                >
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ display: "none" }} // Hide the input
                  />
                  <Button
                    disabled={isLoading}
                    onClick={triggerFileInput}
                    icon={<UploadOutlined />}
                    style={{
                      width: "100%",
                      textOverflow: "ellipsis", // Truncate text
                      overflow: "hidden", // Hide overflow content
                      whiteSpace: "nowrap", // Prevent wrapping to a new line
                    }}
                  >
                    {file ? (
                      <span
                        style={{
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </span>
                    ) : (
                      "Choose to Upload"
                    )}
                  </Button>
                </Form.Item>
                <Button
                  type="default"
                  disabled={isLoading}
                  style={{ marginTop: "1rem" }}
                  onClick={handleDownload}
                  icon={<DownloadOutlined />}
                >
                  Template
                </Button>
              </Col>
            )}
          </Row>
        </FormContent>
      </Form>
    </FormLayout>
  );
}
